import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { channelColors, channelIcons } from "@/constants/channelIcons";
import { api } from "@/lib/api-client";
import { useGetNotificationChannels } from "@/lib/api-client/paths/notification";
import { GetNotificationChannels } from "@/server-types";
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { addToast, Button } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { GripVertical } from "lucide-react";
import { useEffect, useState } from "react";

const NotificationChannelsTab = () => {
  const {
    data,
    processedErrorMessage: getNotificationChannelsErrorMessage,
    isFetching,
    refetch,
  } = api.notification.useGetNotificationChannels();

  const {
    mutateAsync: updateChannels,
    processedErrorMessage: updateChannelsErrorMessage,
    isPending: isUpdatingChannels,
  } = api.notification.useUpdateNotificationChannels();

  const [channels, setChannels] = useState<
    {
      notificationType: "viber" | "whatsapp" | "sms" | "email";
      id: number;
    }[]
  >([]);

  useEffect(() => {
    if (!data) return;
    setChannels(
      data.channels.sort((a, b) => a.fallbackOrder - b.fallbackOrder),
    );
  }, [data]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = channels.findIndex((i) => i.id === active.id);
      const newIndex = channels.findIndex((i) => i.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const newChannels = arrayMove(channels, oldIndex, newIndex);
      setChannels(newChannels);
    }
  };

  const handleUpdateChannels = async () => {
    await updateChannels({
      channels: channels.map((channel) => channel.notificationType),
    });

    addToast({
      title: t`Posodobljeno`,
      description: t`Vrstni red kanalov je bil spremenjen`,
      color: "success",
    });

    refetch();
  };

  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 5,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={channels}>
        <div className="mx-auto flex max-w-[400px] flex-col items-center gap-2 px-4 pt-4">
          <LimeAlert
            className="w-full"
            message={getNotificationChannelsErrorMessage}
          />
          <span className="text-sm text-gray-500">
            <Trans>Spremeni vrstni red kanalov za pošiljanje sporočil</Trans>
          </span>
          {channels.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              label={item.notificationType}
            />
          ))}

          <LimeAlert className="w-full" message={updateChannelsErrorMessage} />
          <Button
            variant="solid"
            className="mt-4 w-full"
            color="primary"
            onPress={handleUpdateChannels}
            isLoading={isUpdatingChannels}
          >
            <Trans>Shrani</Trans>
          </Button>
        </div>
      </SortableContext>
    </DndContext>
  );
};

const SortableItem = ({ id, label }: { id: number; label: string }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transition,
    transform,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const bgClasses = {
    sms: "bg-gray-200",
    viber: "bg-purple-200",
    whatsapp: "bg-green-200",
    email: "bg-gray-200",
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full">
      <div
        className={`flex items-center justify-between gap-2 rounded-md border-1 border-gray-300 p-2 ${bgClasses[label as keyof typeof bgClasses]}`}
      >
        {channelIcons[label as keyof typeof channelIcons]}
        <span>{label}</span>
        <Button variant="light" isIconOnly {...listeners}>
          <GripVertical />
        </Button>
      </div>
    </div>
  );
};

export default NotificationChannelsTab;
